import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Button,
  Text,
  BlockStack,
  InlineStack,
  EmptyState,
  Spinner,
  Modal,
  Toast,
  Badge,
  Checkbox,
  TextField,
  Select,
  Thumbnail,
  Box,
} from '@shopify/polaris';
import { api } from '../api';

const PER_PAGE_OPTIONS = [
  { label: '5 per page', value: '5' },
  { label: '10 per page', value: '10' },
  { label: '25 per page', value: '25' },
  { label: '50 per page', value: '50' },
];

function PaginationBar({ page, totalPages, total, start, end, onPrev, onNext }) {
  if (total === 0) return null;
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <Text variant="bodySm" tone="subdued">
          Showing {start}–{end} of {total} item{total !== 1 ? 's' : ''}
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onPrev} disabled={page <= 1} style={btnStyle(page > 1)}>← Previous</button>
          <Text variant="bodySm" tone="subdued">Page {page} of {totalPages}</Text>
          <button onClick={onNext} disabled={page >= totalPages} style={btnStyle(page < totalPages)}>Next →</button>
        </div>
      </div>
    </Card>
  );
}

function btnStyle(active) {
  return {
    padding: '6px 14px',
    border: '1px solid #c9cccf',
    borderRadius: '6px',
    background: active ? '#fff' : '#f6f6f7',
    color: active ? '#202223' : '#8c9196',
    cursor: active ? 'pointer' : 'not-allowed',
    fontSize: '13px',
    fontFamily: 'sans-serif',
  };
}

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignModal, setAssignModal] = useState(null);
  const [selectedAttrs, setSelectedAttrs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, attrRes, shopifyRes] = await Promise.all([
        api.get('/api/products'),
        api.get('/api/attributes'),
        api.get('/shopify/products').catch(() => ({ data: { data: [] } })),
      ]);
      setProducts(prodRes.data?.data || []);
      setAttributes(attrRes.data?.data || []);
      setShopifyProducts(shopifyRes.data?.data || []);
    } catch {
      setToast({ content: 'Failed to load data', error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset page on search/limit change
  useEffect(() => { setPage(1); }, [searchValue, limit]);

  const openAssign = async (product) => {
    setAssignModal(product);
    try {
      const res = await api.get(`/api/products/${product.id}/attributes`);
      const existing = (res.data?.data || []).map(a => String(a.attribute_id || a.id));
      setSelectedAttrs(existing);
    } catch {
      setSelectedAttrs([]);
    }
  };

  const handleSaveAssignment = async () => {
    setSaving(true);
    try {
      await api.post(`/api/products/${assignModal.id}/attributes`, {
        product_title: assignModal.title,
        attributes: selectedAttrs.map((id, idx) => ({
          attribute_id: id,
          sort_order: idx,
          is_required: false,
        })),
      });
      setToast({ content: `Attributes assigned to "${assignModal.title}"` });
      setAssignModal(null);
      fetchData();
    } catch (err) {
      setToast({ content: 'Failed to save assignments', error: true });
    } finally {
      setSaving(false);
    }
  };

  const toggleAttr = (attrId) => {
    const id = String(attrId);
    setSelectedAttrs(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const handleRemoveProduct = async (productId) => {
    try {
      await api.delete(`/api/products/${productId}`);
      setToast({ content: 'Product configuration removed' });
      fetchData();
    } catch {
      setToast({ content: 'Failed to remove product', error: true });
    }
  };

  const allProducts = shopifyProducts.length > 0 ? shopifyProducts : products;

  const filteredProducts = allProducts.filter(p =>
    !searchValue || p.title?.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Paginate
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / limit));
  const safePage = Math.min(page, totalPages);
  const start = filteredProducts.length === 0 ? 0 : (safePage - 1) * limit + 1;
  const end = Math.min(safePage * limit, filteredProducts.length);
  const paginated = filteredProducts.slice((safePage - 1) * limit, safePage * limit);

  const getProductConfig = (productId) => products.find(p => String(p.product_id) === String(productId));

  const rows = paginated.map(product => {
    const config = getProductConfig(product.id);
    return [
      <InlineStack gap="300" blockAlign="center">
        {product.image ? (
          <Thumbnail source={product.image} alt={product.title} size="small" />
        ) : (
          <div style={{ width: 40, height: 40, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Text tone="subdued">📦</Text>
          </div>
        )}
        <Text variant="bodyMd" fontWeight="semibold">{product.title}</Text>
      </InlineStack>,
      config
        ? <Badge tone="success">{config.attribute_count || '?'} attributes</Badge>
        : <Badge tone="attention">Not configured</Badge>,
      <InlineStack gap="200">
        <Button size="slim" variant="primary" onClick={() => openAssign(product)}>
          {config ? 'Edit Config' : 'Assign Attributes'}
        </Button>
        {config && (
          <Button size="slim" tone="critical" onClick={() => handleRemoveProduct(product.id)}>
            Remove
          </Button>
        )}
      </InlineStack>,
    ];
  });

  const toastMarkup = toast ? (
    <Toast content={toast.content} error={toast.error} onDismiss={() => setToast(null)} />
  ) : null;

  return (
    <>
      {toastMarkup}
      <Page
        title="Products"
        subtitle="Assign custom attributes to your Shopify products"
      >
        <Layout>
          {/* Search + per-page */}
          <Layout.Section>
            <Card>
              <InlineStack gap="300" wrap blockAlign="center">
                <div style={{ flex: 1, minWidth: 200 }}>
                  <TextField
                    label=""
                    placeholder="Search products..."
                    value={searchValue}
                    onChange={setSearchValue}
                    clearButton
                    onClearButtonClick={() => setSearchValue('')}
                    autoComplete="off"
                  />
                </div>
                <div style={{ minWidth: 150 }}>
                  <Select
                    label=""
                    options={PER_PAGE_OPTIONS}
                    value={String(limit)}
                    onChange={v => setLimit(Number(v))}
                  />
                </div>
                <Text variant="bodySm" tone="subdued">
                  {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
                </Text>
              </InlineStack>
            </Card>
          </Layout.Section>

          {/* Table */}
          <Layout.Section>
            <Card padding="0">
              {loading ? (
                <Box padding="800">
                  <div style={{ display: 'flex', justifyContent: 'center' }}><Spinner size="large" /></div>
                </Box>
              ) : filteredProducts.length === 0 ? (
                <EmptyState
                  heading="No products found"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>Products from your Shopify store will appear here once the app is installed.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text']}
                  headings={['Product', 'Configuration', 'Actions']}
                  rows={rows}
                  hoverable
                />
              )}
            </Card>
          </Layout.Section>

          {/* Pagination */}
          {!loading && (
            <Layout.Section>
              <PaginationBar
                page={safePage}
                totalPages={totalPages}
                total={filteredProducts.length}
                start={start}
                end={end}
                onPrev={() => setPage(p => Math.max(1, p - 1))}
                onNext={() => setPage(p => Math.min(totalPages, p + 1))}
              />
            </Layout.Section>
          )}
        </Layout>

        {/* Assign Attributes Modal */}
        <Modal
          open={!!assignModal}
          onClose={() => setAssignModal(null)}
          title={`Assign Attributes — ${assignModal?.title}`}
          primaryAction={{ content: saving ? 'Saving...' : 'Save Assignment', onAction: handleSaveAssignment, loading: saving }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setAssignModal(null) }]}
          large
        >
          <Modal.Section>
            {attributes.length === 0 ? (
              <Text tone="subdued">No attributes available. Create attributes first.</Text>
            ) : (
              <BlockStack gap="400">
                <Text variant="bodyMd" tone="subdued">
                  Select individual attributes or click a group header to select all attributes in that group:
                </Text>

                {(() => {
                  const activeAttrs = attributes.filter(a => a.is_active);
                  const grouped = {};
                  const ungrouped = [];
                  activeAttrs.forEach(attr => {
                    if (attr.group_name) {
                      if (!grouped[attr.group_name]) grouped[attr.group_name] = [];
                      grouped[attr.group_name].push(attr);
                    } else {
                      ungrouped.push(attr);
                    }
                  });

                  const toggleGroup = (groupAttrs) => {
                    const ids = groupAttrs.map(a => String(a.id));
                    const allSelected = ids.every(id => selectedAttrs.includes(id));
                    if (allSelected) {
                      setSelectedAttrs(prev => prev.filter(id => !ids.includes(id)));
                    } else {
                      setSelectedAttrs(prev => [...new Set([...prev, ...ids])]);
                    }
                  };

                  return (
                    <BlockStack gap="300">
                      {Object.entries(grouped).map(([groupName, groupAttrs]) => {
                        const allSelected = groupAttrs.every(a => selectedAttrs.includes(String(a.id)));
                        const someSelected = groupAttrs.some(a => selectedAttrs.includes(String(a.id)));
                        return (
                          <BlockStack key={groupName} gap="200">
                            <div style={{ background: '#f6f6f7', padding: '8px 12px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                              <Checkbox
                                label=""
                                checked={allSelected}
                                onChange={() => toggleGroup(groupAttrs)}
                              />
                              <Text variant="bodyMd" fontWeight="bold">📁 {groupName}</Text>
                              <Badge tone={allSelected ? 'success' : someSelected ? 'attention' : 'new'}>
                                {groupAttrs.filter(a => selectedAttrs.includes(String(a.id))).length}/{groupAttrs.length} selected
                              </Badge>
                              <Button size="micro" onClick={() => toggleGroup(groupAttrs)}>
                                {allSelected ? 'Deselect All' : 'Select All'}
                              </Button>
                            </div>
                            <div style={{ paddingLeft: 24 }}>
                              <BlockStack gap="150">
                                {groupAttrs.map(attr => (
                                  <InlineStack key={attr.id} gap="300" blockAlign="center">
                                    <Checkbox
                                      label=""
                                      checked={selectedAttrs.includes(String(attr.id))}
                                      onChange={() => toggleAttr(attr.id)}
                                    />
                                    <InlineStack gap="200" blockAlign="center">
                                      <Text variant="bodyMd">{attr.label}</Text>
                                      <Badge>{attr.type}</Badge>
                                      {attr.is_required && <Badge tone="critical">Required</Badge>}
                                    </InlineStack>
                                  </InlineStack>
                                ))}
                              </BlockStack>
                            </div>
                          </BlockStack>
                        );
                      })}

                      {ungrouped.length > 0 && (
                        <BlockStack gap="200">
                          {Object.keys(grouped).length > 0 && (
                            <Text variant="bodyMd" fontWeight="bold" tone="subdued">— No Group —</Text>
                          )}
                          {ungrouped.map(attr => (
                            <InlineStack key={attr.id} gap="300" blockAlign="center">
                              <Checkbox
                                label=""
                                checked={selectedAttrs.includes(String(attr.id))}
                                onChange={() => toggleAttr(attr.id)}
                              />
                              <InlineStack gap="200" blockAlign="center">
                                <Text variant="bodyMd">{attr.label}</Text>
                                <Badge>{attr.type}</Badge>
                                {attr.is_required && <Badge tone="critical">Required</Badge>}
                              </InlineStack>
                            </InlineStack>
                          ))}
                        </BlockStack>
                      )}
                    </BlockStack>
                  );
                })()}

                <Text variant="bodySm" tone="subdued">
                  {selectedAttrs.length} attribute(s) selected
                </Text>
              </BlockStack>
            )}
          </Modal.Section>
        </Modal>
      </Page>
    </>
  );
}
